'use client'

import { resolveCollectionSlug } from '@/utils/market/collection-slug'
import CollectionOffersPanel from '@/components/offers/collection-offers-panel'
import React, { FC, ReactElement } from 'react'
import { lowerQuote, useMark } from '@/lib/quotes/hooks'
import useEscrows from '@/utils/hooks/useEscrows'
import useCollection from '@/utils/hooks/useCollection'
import useCollectionStats from '@/utils/hooks/useCollectionStats'
import useCollectionInscriptions from '@/utils/hooks/useCollectionInscriptions'
import useBrc20Balance from '@/utils/hooks/useBrc20Balance'
import formatNumber from '@/utils/format-number'
import { formatSatsPer } from '@/utils/format-sats'
import useTokenPrices from '@/utils/hooks/useTokenPrices'
import isRune from '@/utils/is-rune'
import isAlkanes from '@/utils/is-alkanes'
import isFungible from '@/utils/is-fungible'
import { getTapTickerFromSlug } from '@/utils/tap-token'
import useWallet from '@/utils/hooks/useWallet'
import { NETWORK } from '@/utils/constants'

import TapPrepare from './tap-prepare'
import Wallet from '@/utils/wallet'
import { PreparedTapListings } from '@/utils/tap-listing-plan'
import List from './list'
import Delist from './delist'
import Split from './split'
import InscribeTransfer from './inscribe-transfer'
import Send from './send'
import Refund from './refund'
import Consolidate from './consolidate'

import { CommandItem } from '@/components/ui/command'
import CollectionIcon from '@/components/collection-icon'
import Divider from '../../components/divider'
import GradientButton from '@/components/button-gradient-epic'
import { addRecentSearch, toRecentCollection } from '@/utils/recent-searches'
import { Loading } from '@/components/ui/loading'
import Tabs from './tabs'
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue
} from '@/components/ui/select'
import { getInscriptionCollectionSlugs } from '@/utils/inscription-collections'
import {
	collectionObjectPagePath,
	getCollectionObjectIds,
	isCollectionObjectSlug
} from '@/utils/collection-object'

const _MARKET_FEE_RATE = 2.7
const _ARTIST_ROYALTY = 4.2

const TABS = {
	list: List,
	delist: Delist,
	split: Split,
	inscribeTransfer: InscribeTransfer,
	send: Send,
	refund: Refund,
	consolidate: Consolidate
}

const CollectionDetail: FC<{ onNavigate: (v: string) => void; onClose: () => void; args?: any }> = (
	props
): ReactElement => {
	const { onClose: _onClose, args, onNavigate } = props

	const slug: any = resolveCollectionSlug(args?.slug)
	const isCollectionObject = isCollectionObjectSlug(slug)
	const isVirtual = slug === 'frozen' || slug === 'uncategorized'
	const classification = !isFungible(slug)
		? 'Inscriptions'
		: isRune(slug)
		? 'Runes'
		: isAlkanes(slug)
		? 'Alkanes'
		: (getTapTickerFromSlug(slug) || slug.split('-')[1]).toUpperCase()
	const currency = isFungible(slug) ? 'sats' : NETWORK

	//
	// Local State
	//

	const [tab, setTab] = React.useState(args.tab || 'list')
	const [prepared, setPrepared] = React.useState<PreparedTapListings | undefined>()
	const [collectionFilter, setCollectionFilter] = React.useState('all')

	//
	// Remote Data
	//

	const { data: tokenPrices } = useTokenPrices()
	const { data: escrows } = useEscrows(isVirtual ? null : slug)
	const { data: collectionData } = useCollection(isVirtual ? null : slug)
	const { data: collectionStats } = useCollectionStats(isVirtual ? null : slug)
	const { data: tokenData, isLoading: isLoadingTokenData } = useBrc20Balance()
	const token = React.useMemo(
		() => tokenData.find((e) => e?.collection?.slug === slug),
		[slug, tokenData]
	)
	const { data: rawInscriptionData, isLoading: isLoadingInscriptionData } = useWallet()
	const { data: objectInscriptions, isLoading: isLoadingObjectInscriptions } =
		useCollectionInscriptions(isCollectionObject ? slug : '', isCollectionObject)
	// Stabilize inscriptionData reference — useWallet creates a new object every render
	const inscriptionDataJson = JSON.stringify(rawInscriptionData?.inscriptions?.map((e) => e.id))
	const inscriptionData = React.useMemo(() => {
		return rawInscriptionData
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [inscriptionDataJson])

	const inscriptionDataMap = React.useMemo(() => {
		const map: Record<string, number> = {}
		if (!inscriptionData || !Array.isArray(inscriptionData.inscriptions)) {
			return map
		}

		for (let e of inscriptionData.inscriptions) {
			const slugs = getInscriptionCollectionSlugs(e)
			if (slugs.length === 0) {
				map.uncategorized = (map.uncategorized || 0) + 1
			}
			for (const key of slugs) {
				map[key] = (map[key] || 0) + 1
			}
			map['frozen'] = (map['frozen'] || 0) + 1
		}

		return map
	}, [inscriptionData])

	const objectMemberIds = React.useMemo(
		() => getCollectionObjectIds(objectInscriptions),
		[objectInscriptions]
	)

	const objectOwnedCount = React.useMemo(() => {
		if (!isCollectionObject || !inscriptionData || !Array.isArray(inscriptionData.inscriptions)) {
			return 0
		}
		const members = new Set(objectMemberIds)
		if (members.size === 0) return 0
		return inscriptionData.inscriptions.filter((item: any) => members.has(item.id)).length
	}, [inscriptionData, isCollectionObject, objectMemberIds])

	// Collection filter data for frozen/manage inscriptions
	const frozenCollections = React.useMemo(() => {
		if (!inscriptionData || !Array.isArray(inscriptionData.inscriptions)) return []
		const map = new Map<string, { collection: any; count: number }>()
		let uncategorizedCount = 0
		for (const item of inscriptionData.inscriptions) {
			const slugs = getInscriptionCollectionSlugs(item)
			if (slugs.length === 0) {
				uncategorizedCount++
				continue
			}
			for (const colSlug of slugs) {
				const existing = map.get(colSlug)
				if (existing) {
					existing.count++
				} else {
					map.set(colSlug, { collection: item.collection, count: 1 })
				}
			}
		}
		const result: { slug: string; name: string; collection: any; count: number }[] = []
		map.forEach((v, s) =>
			result.push({
				slug: s,
				name: v.collection?.slug === s ? v.collection.name : s,
				collection: v.collection,
				count: v.count
			})
		)
		if (uncategorizedCount > 0) {
			result.push({
				slug: 'uncategorized',
				name: 'Uncategorized',
				collection: null,
				count: uncategorizedCount
			})
		}
		result.sort((a, b) => b.count - a.count)
		return result
	}, [inscriptionData])

	const totalInscriptionCount = React.useMemo(() => {
		if (!inscriptionData || !Array.isArray(inscriptionData.inscriptions)) return 0
		return inscriptionData.inscriptions.length
	}, [inscriptionData])

	//
	// Memos
	//

	const collection = React.useMemo(() => {
		if (slug === 'frozen') {
			return { name: 'Manage Inscriptions', slug: 'frozen' }
		}
		if (slug === 'uncategorized') {
			return { name: 'Uncategorized', slug: 'uncategorized' }
		}
		return { ...collectionData, ...collectionStats }
	}, [collectionData, collectionStats, slug])

	const mark = useMark(slug)
	const { floorSats, floorPer, floorPriceSats } = React.useMemo(() => {
		const globalPrice = tokenPrices?.find((e) => e.slug === slug)

		if (!escrows && !Array.isArray(escrows) && !globalPrice) {
			return {}
		}

		let min

		if (escrows && Array.isArray(escrows)) {
			for (let e of escrows) {
				const per = e.satoshi_price / parseFloat(e.amount || 1)
				if (per < (min as any) || !min) {
					min = per
				}
			}
		}

		if (globalPrice && min) {
			min = Math.min(globalPrice.floor_price_per, min)
		} else if (globalPrice) {
			min = parseFloat(globalPrice.floor_price_per)
		}

		// The fair value caps the floor: a listing is priced to sell, and a
		// piece does not sell above where its kind has been clearing.
		const capped = lowerQuote(mark?.fair_sats, min)
		if (!capped) {
			return {}
		}
		min = capped

		const { sats, per } = formatSatsPer(min)

		if (per === 'sats' && currency === NETWORK) {
			return { floorSats: parseInt(sats.toString()) / 1e8, floorPer: NETWORK, floorPriceSats: min }
		}

		return { floorSats: sats, floorPer: per, floorPriceSats: min }
	}, [currency, escrows, mark?.fair_sats, slug, tokenPrices])

	//
	// Handlers
	//

	// Set initial filter for consolidate tab once data is loaded. The filter is
	// user-owned state after that, so this is a genuine external-data reaction,
	// not a derivable value.
	React.useEffect(() => {
		if (args.tab === 'consolidate' && frozenCollections.length > 0) {
			const hasUncategorized = frozenCollections.some((c) => c.slug === 'uncategorized')
			// eslint-disable-next-line react-hooks/set-state-in-effect
			setCollectionFilter(hasUncategorized ? 'uncategorized' : 'all')
		}
	}, [args.tab, frozenCollections])

	// Latest-value ref so handleChangeTab stays identity-stable; written from
	// an effect, never during render.
	const frozenCollectionsRef = React.useRef(frozenCollections)
	React.useEffect(() => {
		frozenCollectionsRef.current = frozenCollections
	}, [frozenCollections])
	const handleChangeTab = React.useCallback((v: any) => {
		setTab(v)
		if (v === 'consolidate') {
			const hasUncategorized = frozenCollectionsRef.current.some((c) => c.slug === 'uncategorized')
			setCollectionFilter(hasUncategorized ? 'uncategorized' : 'all')
		} else {
			setCollectionFilter('all')
		}
	}, [])
	const description =
		isCollectionObject && objectOwnedCount > 0
			? `Balance: ${objectOwnedCount}`
			: !isFungible(slug) && (inscriptionDataMap as any)[slug]
			? `Balance: ${(inscriptionDataMap as any)[slug]}`
			: token
			? `Balance: ${formatNumber(token.overall_balance ?? 0)}`
			: ``

	const tokenForComponent = React.useMemo(() => {
		if (!isCollectionObject) return token || collection
		return {
			...collection,
			collection_object_member_ids: objectMemberIds
		}
	}, [collection, isCollectionObject, objectMemberIds, token])

	if (!collection) {
		return <React.Fragment />
	}

	const Component =
		slug.startsWith('tap-') && tab === 'split'
			? TapPrepare
			: (TABS as any)[
					tab === 'split' && !(isRune(slug) || isAlkanes(slug)) ? 'inscribeTransfer' : tab
			  ]

	return (
		<>
			<input type="text" style={{ display: 'none' }} />
			{!isVirtual && (
				<>
					<CommandItem
						value={collection.id || collection.slug}
						onSelect={() => onNavigate(collectionObjectPagePath(collection.slug))}
						className="items-center gap-3"
					>
						<CollectionIcon
							collection={collection}
							className="size-8 shrink-0 rounded-full overflow-hidden"
						/>
						<div className="flex min-w-0 flex-1 flex-col justify-center gap-0.5">
							<span className="truncate text-sm">{collection.name}</span>
							{description && (
								<span className="truncate text-xxs text-muted-foreground">{description}</span>
							)}
						</div>
						{floorSats && (
                            <div className="flex shrink-0 flex-col items-start gap-0.5 px-2 text-left">
                                <span className="text-[10px] leading-3 text-muted-foreground">Floor</span>
                                <span className="text-xs leading-4 tabular-nums">
                                    {floorSats} <span className="text-muted-foreground">{floorPer}</span>
                                </span>
                            </div>
                        )}
						<GradientButton
							type="button"
							className="h-8 shrink-0 !rounded-full px-4 text-xs"
							onClick={(evt) => {
								evt.stopPropagation()
								addRecentSearch({
									slug: collection.slug,
									name: collection.name,
									collection: toRecentCollection(collection)
								})
								onNavigate(collectionObjectPagePath(collection.slug))
							}}
						>
							Buy now
						</GradientButton>
					</CommandItem>
					<Divider />
				</>
			)}
			{slug === 'frozen' && (
				<>
					<div className="flex px-2 h-7 w-full justify-between items-center">
						<p className="text-muted-foreground text-xs font-medium">My Wallet</p>
					</div>
					<div className="flex px-2 py-1.5 gap-2 items-center justify-between">
						<Tabs value={tab} onValueChange={handleChangeTab} slug={slug} />
						<Select value={collectionFilter} onValueChange={setCollectionFilter}>
							<SelectTrigger className="h-7 max-w-[180px] bg-transparent border-border text-muted-foreground text-xs px-2 overflow-hidden">
								<span className="truncate">
									<SelectValue />
								</span>
							</SelectTrigger>
							<SelectContent className="bg-card border-border max-h-[200px]">
								<SelectItem value="all" className="text-foreground text-xs hover:bg-accent">
									All ({totalInscriptionCount})
								</SelectItem>
								{frozenCollections.map((c) => (
									<SelectItem
										key={c.slug}
										value={c.slug}
										className="text-foreground text-xs hover:bg-accent"
									>
										<div className="flex items-center gap-2 whitespace-nowrap">
											{c.collection ? (
												<CollectionIcon
													collection={c.collection}
													className="size-4 min-w-4 rounded-full overflow-hidden"
												/>
											) : (
												<div className="size-4 min-w-4 rounded-full bg-accent" />
											)}
											<span className="truncate max-w-[120px]">{c.name}</span>
											<span className="text-muted-foreground">({c.count})</span>
										</div>
									</SelectItem>
								))}
							</SelectContent>
						</Select>
					</div>
				</>
			)}
			{(!isLoadingTokenData && !token && isFungible(slug)) ||
			(!isLoadingInscriptionData &&
				!isLoadingObjectInscriptions &&
				!isFungible(slug) &&
				slug !== 'frozen' &&
				!isCollectionObject &&
				!(inscriptionDataMap as any)[slug]) ||
			(!isLoadingInscriptionData &&
				!isLoadingObjectInscriptions &&
				isCollectionObject &&
				objectOwnedCount === 0) ||
			(!isLoadingInscriptionData && slug === 'frozen' && totalInscriptionCount === 0) ? (
				<div style={{ padding: '16px', textAlign: 'center', color: '#666' }}>No Balance</div>
			) : (isLoadingTokenData && isFungible(slug)) ||
			  (isLoadingInscriptionData && !isFungible(slug)) ||
			  (isLoadingObjectInscriptions && isCollectionObject) ? (
				<div className="flex items-center justify-center min-h-[200px]">
					<Loading size={16} />
				</div>
			) : (
				((isFungible(slug) && token) ||
					(!isFungible(slug) &&
						((isCollectionObject && objectOwnedCount > 0) ||
							(inscriptionDataMap as any)[slug] ||
							(slug === 'frozen' && totalInscriptionCount > 0)))) &&
				Component && (
					<div className="min-h-[200px]">
						<Component
							key={`${slug}:${Wallet.getTaprootAddress()}`}
							{...props}
							marketActions={!isVirtual && !isFungible(slug) && (
                <div className="px-2 py-1">
                    <CollectionOffersPanel
                        actionsOnly
                        hideUnavailableSell
                        slug={slug}
                        name={collection.name}
                        collection={collection}
                        floorSats={floorPriceSats ?? null}
                    />
                </div>
            )}
							prepared={prepared}
							onPrepared={(value: PreparedTapListings) => {
								setPrepared(value)
								setTab('list')
							}}
							askingPriceSats={floorPriceSats}
							floorSats={floorSats}
							tab={tab}
							onChangeTab={handleChangeTab}
							token={tokenForComponent}
							classification={classification}
							collectionFilter={collectionFilter}
						/>
					</div>
				)
			)}
		</>
	)
}

export default CollectionDetail
